/**
 * db.service.ts
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import { serialize, deserialize } from "bson";
import moment from "moment-timezone";
import { Membership, UserScopes } from "../models/auth";

import { Group, StoredKeys, DB_VERSION } from "../models/group";
import { DisplayMessage, MessageType } from "../models/message";
import { Reaction } from "../models/reaction";
import { MessageDraft } from "../models/messageDraft";

import { cryptoServiceFactory, CryptoService } from "./crypto.service";
import { DateTime } from "luxon";

export interface DbService {
  readonly DB_VERSION: number,
  open(userID: string, decryptKey: ArrayBuffer, nonce: ArrayBuffer): Promise<void>,
  checkMigrate(): Promise<void>,
  close(): void,
  getKeys(deviceID: string): Promise<StoredKeys | null>,
  saveNewKeys(keys: StoredKeys): Promise<void>,
  getGroups(accountID: string, groupType: "chat" | "meeting"): Promise<Group[]>,
  countUnreadReplies(accountID: string): Promise<number>,
  getGroup(groupID: string): Promise<Group | null>,
  saveGroup(group: Group): Promise<void>,
  updateGroup(groupID: string, update: Partial<Group>): Promise<Group>,
  deleteGroup(groupID: string): Promise<void>,
  saveMessage(frameID: string, accountID: string, message: DisplayMessage): Promise<void>,
  getMessage(msgID: string): Promise<DisplayMessage | null>,
  getMessageFromFrame(frameID: string): Promise<DisplayMessage | null>,
  getMessages(groupID: string, starting: number, limit: number): Promise<Array<DisplayMessage>>,
  getLatestThreadMessages(accountID: string, starting: number, limit: number): Promise<Array<DisplayMessage>>,
  updateMessage(msgID: string, accountID: string, update: (msg: DisplayMessage | null) => Promise<DisplayMessage | null>): Promise<DisplayMessage>,
  deleteMessage(msgID: string): Promise<void>,
  getNewestMsgDate(groupID: string): Promise<DateTime | null>,
  deleteContent(groupID: string): Promise<void>,
  markMessagesAsRead(groupID: string, starting: number): Promise<void>,
  saveReply(frameID: string, accountID: string, message: DisplayMessage): Promise<void>,
  getReply(replyID: string): Promise<DisplayMessage | null>,
  getReplies(threadID: string): Promise<Array<DisplayMessage>>,
  getReplyFromFrame(frameID: string): Promise<DisplayMessage | null>,
  countUnreadParticipatingThreadsForAccount(accountID: string): Promise<[number, number]>,
  updateReply(replyID: string, update: (msg: DisplayMessage | null) => Promise<DisplayMessage | null>): Promise<DisplayMessage>,
  deleteReply(replyID: string): Promise<void>,
  markThreadAsRead(threadID: string): Promise<boolean>,
  getReaction(reactionID: string): Promise<Reaction | null>,
  getRecentMainReactions(newerThan: Date | undefined, olderThan?: Date): Promise<Array<Reaction>>,
  getReactionsForMessage(msgID: string): Promise<Array<Reaction>>,
  getReactionsForThread(threadID: string): Promise<Array<Reaction>>,
  saveReaction(accountID: string, groupID: string, reaction: Reaction): Promise<void>,
  removeReaction(reactionID: string): Promise<void>,
  getDraft(_id: string): Promise<MessageDraft | null>,
  getDrafts(accountID: string): Promise<Array<MessageDraft>>,
  upsertDraft(draft: MessageDraft): Promise<void>,
  upsertDraftContent(accountID: string, groupID: string, threadID: string | null | undefined, content: string): Promise<void>,
  deleteDraft(groupID: string, threadID: string | null | undefined): Promise<void>
}

class ParentDBService {
  dbVersion: number;
  dbname: string;

  constructor(dbVersion: number) {
    this.dbVersion = dbVersion;
    this.dbname = "";
  }

  async getDB(): Promise<IDBDatabase> {
    const db = await openDB(this.dbname, this.dbVersion, this.versionUpdater);
    db.onversionchange = this.dbNeedsUpgrade;
    return db;
  }

  dbNeedsUpgrade() {
    // make lint happy
  }
  versionUpdater(db: IDBDatabase, oldVersion: number, req: IDBOpenDBRequest): void {
    console.log("must override this:", db, oldVersion, req);
  }

  async openAndRead<T>(stores: string | string[], readFn: ReadTransactionFn<T>): Promise<T> {
    const db = await this.getDB();
    const result = await readTransaction(db, stores, readFn);
    db.close();
    return result;
  }

  async openAndReadID<T>(stores: string, _id: string, readFn: (result: any) => T | Promise<T>): Promise<T> {
    const db = await this.getDB();
    const result = await readWithID(db, stores, _id, readFn);
    db.close();
    return result;
  }

  async openAndWrite(stores: string | string[], writeFn: UpdateTransactionFn): Promise<any> {
    const db = await this.getDB();
    const result = await updateTransaction(db, stores, writeFn);
    db.close();
    return result;
  }
}

interface MessageContainer {
  _id: string,
  accountID: string,
  groupID: string,
  threadID?: string,
  frameID: string,
  msgtype: MessageType,
  isUnread: 0 | 1,
  isMention: 0 | 1,
  participatingInThread: 0 | 1,
  hideThread: 0 | 1,
  lastReply: Date | undefined,
  ciphertext: Uint8Array,
  created: Date
}

class MsgDbServiceImpl extends ParentDBService implements DbService {
  readonly DB_VERSION: number;
  decryptKey?: CryptoKey;
  dbNonce: ArrayBuffer;
  cryptoService: CryptoService;
  needMigration18: boolean;

  constructor() {
    super(DB_VERSION);
    this.DB_VERSION = DB_VERSION;
    this.dbname = "";
    this.dbNonce = new ArrayBuffer(0);
    this.needMigration18 = false;
    this.versionUpdater = this.versionUpdater.bind(this);
    this.dbNeedsUpgrade = this.dbNeedsUpgrade.bind(this);
    this.saveReply = this.saveReply.bind(this);
    this.saveMessage = this.saveMessage.bind(this);
    this.unpackGroup = this.unpackGroup.bind(this);
    this.unpackMessage = this.unpackMessage.bind(this);

    this.cryptoService = cryptoServiceFactory();
  }

  async open(userID: string, decryptKey: ArrayBuffer, nonce: ArrayBuffer): Promise<void> {
    this.dbname = `irisDB-${userID}`;
    this.dbNonce = nonce;
    const key = await this.cryptoService.importCryptoKey(decryptKey)
    this.decryptKey = key;
  }

  close(): void {
    this.decryptKey = undefined;
    this.dbname = "";
  }

  dbNeedsUpgrade() {
    this.dbNonce = new ArrayBuffer(0);
  }

  versionUpdater(db: IDBDatabase, oldVersion: number, req: IDBOpenDBRequest): void {
    let objectStore: IDBObjectStore;
    if (oldVersion < 1) {
      // Build new DB
      objectStore = db.createObjectStore("groups", { keyPath: "_id" });
      objectStore.createIndex("account", "accountID", { unique: false });
      objectStore = db.createObjectStore("messages", { keyPath: "_id" });
      objectStore.createIndex("group", "groupID", { unique: false });
      objectStore = db.createObjectStore("keys", { keyPath: "deviceID" });
    }

    if (oldVersion < 13) {
      objectStore = db.createObjectStore("replies", { keyPath: "_id" });
      objectStore.createIndex("thread", "threadID", { unique: false });
      objectStore.createIndex("created", "created", { unique: false });
    }

    if (oldVersion < 15) {
      req.transaction?.objectStore("groups").clear();
      req.transaction?.objectStore("messages").clear();
      req.transaction?.objectStore("replies").clear();
    }

    if (oldVersion < 16) {
      //objectStore = db.createObjectStore("datakeys", {keyPath: "_id"});
      if (req.transaction) {
        objectStore = req.transaction.objectStore("messages");
        objectStore.createIndex("frame", "frameID", { unique: true });
        objectStore = req.transaction.objectStore("replies");
        objectStore.createIndex("frame", "frameID", { unique: true });
      }
    }
    if (oldVersion < 19) {
      if (req.transaction) {
        objectStore = req.transaction.objectStore("groups");
        objectStore.createIndex("account, grouptype", ["accountID", "grouptype"], { unique: false });
        //console.log("setting version 18 update");
        this.needMigration18 = true;
      }
    }
    if (oldVersion < 20) {
      if (req.transaction) {
        objectStore = req.transaction.objectStore("replies");
        objectStore.createIndex("group", "groupID", { unique: false });

        objectStore = db.createObjectStore("drafts", { keyPath: "_id" });
        objectStore.createIndex("group", "groupID", { unique: false });
        objectStore.createIndex("account", "accountID", { unique: false });
      }
    }

    if (oldVersion < 23) {
      if (req.transaction) {
        objectStore = req.transaction.objectStore("replies");
        objectStore.createIndex("unreadIdx", ["accountID", "isUnread"], { unique: false });

        objectStore = db.createObjectStore("reactions", { keyPath: "_id" });
        objectStore.createIndex("group", "groupID", { unique: false });
        objectStore.createIndex("account", "accountID", { unique: false });
        objectStore.createIndex("message", "msgID", { unique: false });
        objectStore.createIndex("thread", "threadID", { unique: false });
        objectStore.createIndex("created", "created", { unique: false });
      }
    }

    if (oldVersion < DB_VERSION) {
      if (req.transaction) {
        objectStore = req.transaction.objectStore("messages");
        objectStore.createIndex("replyIdx", ["accountID", "lastReply"], { unique: false });
      }
    }

    if (oldVersion > DB_VERSION) {
      // another tab updated the DB, this tab has old software
      throw Error("A newer version exists! Please, refresh this browser tab");
    }
  }

  checkMigrate(): Promise<void> {
    console.log("need migrate:", this.needMigration18);
    if (this.needMigration18) {
      return this.migration18();
    }
    console.log("finished migrate check");
    return bluebird.resolve();
  }

  async migration18(): Promise<void> {
    const typeMap = new Map<string, string>();
    await this.openAndRead<void[]>("groups", transaction => {
      return new bluebird<void[]>((resolve, reject) => {
        const results: Promise<void>[] = [];
        const objectStore = transaction.objectStore("groups");

        // we must wait for devices to populate the new [account,grouptype] index over time
        // otherwise, we'll skip groups that don't have grouptype set in the metadata
        const request = objectStore.openCursor();
        request.onerror = () => reject(request.error);
        request.onsuccess = evt => {
          // called once per cursor value
          const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
          if (cursor) {
            const val = cursor.value;
            if (!val.grouptype) {
              const prom = this.cryptoService.open(
                val.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
                .then(buf => deserialize(buf, { promoteBuffers: true, promoteValues: true }) as Group)
                .then(g => {
                  typeMap.set(g._id, g.grouptype);
                });
              results.push(prom);
            }
            cursor.continue();
          } else {
            resolve(bluebird.all(results));
          }
        };
      });
    });

    await this.openAndWrite("groups", transaction => {
      const objectStore = transaction.objectStore("groups");
      const request = objectStore.openCursor();

      request.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        const val = cursor.value;
        const grouptype = typeMap.get(val._id);
        if (!val.grouptype && grouptype) {
          val.grouptype = grouptype;
          cursor.update(val);
        }
        cursor.continue();
      };
    });

    this.needMigration18 = false;
  }

  async getKeys(deviceID: string): Promise<StoredKeys | null> {

    const buf: Uint8Array | null = await this.openAndRead<any>("keys", (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("keys");
      return new bluebird<Promise<ArrayBuffer> | null>((resolve, reject) => {
        const request = objectStore.get(deviceID);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          if (!request.result) {
            return resolve(null);
          }
          return resolve(this.cryptoService.open(
            request.result.buf, this.decryptKey as CryptoKey, this.dbNonce));
        };
      });
    });

    if (!buf) { return buf; }
    const keys = deserialize(buf, { promoteBuffers: true, promoteValues: true });
    keys.created = moment(keys.created);
    return keys as StoredKeys;
  }

  async saveNewKeys(keys: StoredKeys): Promise<void> {
    const toSerialize = {
      publicKey: keys.publicKey,
      privateKey: keys.privateKey,
      kyberPublicKey: keys.kyberPublicKey,
      kyberPrivateKey: keys.kyberPrivateKey,
      diPublicKey: keys.diPublicKey,
      diPrivateKey: keys.diPrivateKey,
      created: keys.created.toDate()
    }

    const buf = await this.cryptoService.seal(serialize(toSerialize),
      this.decryptKey as CryptoKey, this.dbNonce);

    await this.openAndWrite("keys", (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("keys");
      objectStore.put({
        deviceID: keys.deviceID,
        buf: buf
      });
    });
  }

  async saveGroup(group: Group): Promise<void> {
    if (!group._id) {
      throw new Error("Group is missing _id property. Cannot Save!");
    }
    if (group.created && moment.isMoment(group.created)) {
      group.created = group.created.toDate();
    }

    if (group.lastMsgTime) {
      if (DateTime.isDateTime(group.lastMsgTime)) {
        if (!group.lastMsgTime.toJSDate || typeof group.lastMsgTime.toJSDate !== "function") {
          delete (group as any).lastMsgTime;
        } else {
          (group as any).lastMsgTime = group.lastMsgTime.toJSDate();
        }
      } else if (moment.isMoment(group.lastMsgTime)) {
        (group as any).lastMsgTime = (group.lastMsgTime as moment.Moment).toDate();
      }
    }
    const buf = await this.cryptoService.seal(
      serialize(group), this.decryptKey as CryptoKey, this.dbNonce);

    await this.openAndWrite("groups", transaction => {
      const objectStore = transaction.objectStore("groups");
      objectStore.put({
        _id: group._id,
        accountID: group.accountID,
        grouptype: group.grouptype,
        protoVersion: group.protoVersion,
        ciphertext: buf
      });
    });
  }

  getGroup(groupID: string): Promise<Group | null> {
    if (!this.dbname) { return bluebird.resolve(null); }
    return this.openAndReadID("groups", groupID, async (result: any): Promise<Group | null> => {
      if (!result) { return null; }
      const buf = await this.cryptoService.open(
        result.ciphertext, this.decryptKey as CryptoKey, this.dbNonce);
      const group = deserialize(buf, { promoteBuffers: true, promoteValues: true }) as Group;
      this.unpackGroup(group);
      return group;
    });
  }

  // can't hold the transaction open while re-encrypting the data
  async updateGroup(groupID: string, update: Partial<Group>): Promise<Group> {
    if (!this.dbname) { return bluebird.reject(new Error("userID not configured in DB")); }
    const db = await this.getDB();

    if (update.created && moment.isMoment(update.created)) {
      update.created = update.created.toDate();
    }
    if (update.lastMsgTime && DateTime.isDateTime(update.lastMsgTime)) {
      update.lastMsgTime = update.lastMsgTime.toJSDate() as any;
    }
    // fetch the group in one transaction
    const group = await readWithID<Group | null>(db, "groups", groupID, (result: any) => {
      if (!result) { return bluebird.resolve(null); }
      return this.cryptoService.open(
        result.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
        .then(buf => {
          return Object.assign(deserialize(buf, { promoteBuffers: true, promoteValues: true }), update) as Group;
        });
    });

    if (!group) {
      throw new Error("Group not found");
    }

    // save the group in a second transaction
    const accountID = group.accountID;
    const buf = await this.cryptoService.seal(serialize(group), this.decryptKey as CryptoKey, this.dbNonce);

    await updateTransaction(db, "groups", (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("groups");
      const request = objectStore.get(groupID);
      request.onerror = () => {
        console.error("Failed getting group to update:", request.error);
      };
      request.onsuccess = () => {
        objectStore.put({
          _id: groupID,
          accountID: accountID,
          grouptype: group.grouptype,
          protoVersion: group.protoVersion,
          ciphertext: buf
        });
      }
    });
    db.close();

    this.unpackGroup(group);
    return group;
  }

  deleteGroup(groupID: string): Promise<void> {
    if (!this.dbname) { return bluebird.reject(new Error("userID not configured in DB")); }
    return this.openAndWrite("groups", (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("groups");
      objectStore.delete(groupID);
    });
  }

  getGroups(accountID: string, groupType: "chat" | "meeting"): Promise<Group[]> {
    if (!this.dbname) { return bluebird.resolve([]); }
    return this.openAndRead<Group[]>("groups", transaction => {
      return new bluebird<Group[]>((resolve, reject) => {
        const results: Promise<Group>[] = [];
        const objectStore = transaction.objectStore("groups");

        // we must wait for devices to populate the new [account,grouptype] index over time
        // otherwise, we'll skip groups that don't have grouptype set in the metadata
        const index = objectStore.index("account");
        const range = IDBKeyRange.only(accountID);
        const request = index.openCursor(range);
        request.onerror = () => reject(request.error);
        request.onsuccess = evt => {
          // called once per cursor value
          const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
          if (cursor) {
            const val = cursor.value;
            const prom = this.cryptoService.open(
              val.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
              .then(buf => deserialize(buf, { promoteBuffers: true, promoteValues: true }) as Group)
              .then(g => {
                if (g.created) {
                  if ((g.created as any)._d) {
                    g.created = moment((g.created as any)._d);
                  } else if (!moment.isMoment(g.created)) {
                    g.created = moment(g.created);
                  }
                }
                if (g.lastMsgTime && !DateTime.isDateTime(g.lastMsgTime)) {
                  g.lastMsgTime = DateTime.fromJSDate(g.lastMsgTime as Date);
                }
                return g;
              });
            results.push(prom);
            cursor.continue();
          } else {
            if (groupType === "chat") {
              resolve(bluebird.all(results).filter(g => g.grouptype !== "meeting"));
            } else {
              resolve(bluebird.all(results).filter(g => g.grouptype === "meeting"));
            }
          }
        };
      });
    }).then(groups => { groups.forEach(this.unpackGroup); return groups; });
  }

  private unpackGroup(group: Group): Group {
    if (group.created) {
      if ((group.created as any)._d) {
        group.created = moment((group.created as any)._d);
      } else if (!moment.isMoment(group.created)) {
        group.created = moment(group.created);
      }
    }
    if (group.lastMsgTime) {
      if ((group.lastMsgTime as any)._d) {
        group.lastMsgTime = DateTime.fromJSDate((group.lastMsgTime as any)._d);
      } else if (group.lastMsgTime instanceof Date) {
        group.lastMsgTime = DateTime.fromJSDate(group.lastMsgTime);
      }
    }
    return group;
  }

  countUnreadReplies(accountID: string): Promise<number> {
    return this.openAndRead<number>("replies", transaction => {
      return new bluebird<number>((resolve, reject) => {
        let count = 0;
        const objectStore = transaction.objectStore("replies");
        const index = objectStore.index("unreadIdx");
        const range = IDBKeyRange.only(accountID);
        const request = index.openCursor(range);
        request.onerror = () => reject(request.error);
        request.onsuccess = evt => {
          // called once per cursor value
          const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
          if (cursor) {
            if (cursor.value.isUnread) {
              count++;
            }
            cursor.continue();
          } else {
            resolve(count);
          }
        };
      });
    });
  }

  async saveMessage(frameID: string, accountID: string, message: DisplayMessage): Promise<void> {
    message = this.prepareMessageForSave(message);
    message.frameID = frameID;

    const buf = await this.cryptoService.seal(serialize(message),
      this.decryptKey as CryptoKey, this.dbNonce);

    await this.openAndWrite("messages", (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("messages");
      objectStore.put(this.messageContainerFor(accountID, frameID, message, buf));
    })
  }

  getMessage(msgID: string): Promise<DisplayMessage | null> {
    return this._getMessage(msgID, "messages");
  }

  _getMessage(msgID: string, tableName: string): Promise<DisplayMessage | null> {
    return this.openAndRead<DisplayMessage | null>(tableName, async (transaction: IDBTransaction) => {
      let unread: 0 | 1 = 0;
      let mention: 0 | 1 = 0;
      let participatingInThread: 0 | 1 = 0;
      const buf = await new bluebird<Uint8Array | null>((resolve, reject) => {
        const objectStore = transaction.objectStore(tableName);
        const request = objectStore.get(msgID);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          if (!request.result) {
            return resolve(null);
          }
          const container = request.result as MessageContainer;
          unread = container.isUnread;
          mention = container.isMention;
          participatingInThread = container.participatingInThread;
          resolve(this.cryptoService.open(
            container.ciphertext, this.decryptKey as CryptoKey, this.dbNonce))
        }
      });

      if (!buf) { return buf; }
      const msg = this.unpackMessage(
        deserialize(buf, { promoteBuffers: true, promoteValues: true }) as DisplayMessage,
        unread,
        mention,
        participatingInThread);

      return msg;
    });
  }

  getMessageFromFrame(frameID: string): Promise<DisplayMessage | null> {
    return this.openAndRead<DisplayMessage | null>("messages", async (transaction: IDBTransaction) => {
      let unread: 0 | 1 = 0;
      let mention: 0 | 1 = 0;
      let participatingInThread: 0 | 1 = 0;
      const buf = await new bluebird<Uint8Array | null>((resolve, reject) => {
        const objectStore = transaction.objectStore("messages");
        const index = objectStore.index("frame");
        const request = index.get(frameID);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          if (!request.result) {
            return resolve(null);
          }
          const container = request.result as MessageContainer;
          unread = container.isUnread;
          mention = container.isMention;
          participatingInThread = container.participatingInThread;
          return resolve(this.cryptoService.open(
            container.ciphertext, this.decryptKey as CryptoKey, this.dbNonce))
        }
      });
      if (!buf) { return buf; }
      return this.unpackMessage(
        deserialize(buf, { promoteBuffers: true, promoteValues: true }) as DisplayMessage,
        unread,
        mention,
        participatingInThread);
    });
  }

  async getMessages(groupID: string, starting: number, limit: number): Promise<Array<DisplayMessage>> {
    const db = await this.getDB();
    const msgs = await new Promise<DisplayMessage[]>((resolve, reject) => {

      const transaction = db.transaction("messages");

      const results: Promise<DisplayMessage>[] = [];

      transaction.onerror = function () { reject(transaction.error); };
      transaction.onabort = function () {
        console.warn("transaction aborted");
        reject(transaction.error);
      };
      transaction.oncomplete = function () { resolve(bluebird.all(results)); };

      let haveNotStarted = true;
      const objectStore = transaction.objectStore("messages");
      const index = objectStore.index("group");
      const range = IDBKeyRange.only(groupID);
      const request = index.openCursor(range, "prev");
      request.onerror = () => reject(request.error);
      request.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        if (haveNotStarted && starting > 0) {
          cursor.advance(starting);
          haveNotStarted = false;
          return;
        }
        if (results.length < limit) {
          const msgContainer = cursor.value as MessageContainer; // save value, cursor gets cleaned before then() fires
          const prom = this.cryptoService.open(
            msgContainer.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
            .then(buf => deserialize(buf, { promoteBuffers: true, promoteValues: true }) as DisplayMessage)
            .then(msg => {
              return this.unpackMessage(msg, msgContainer.isUnread, msgContainer.isMention, msgContainer.participatingInThread) as DisplayMessage;
            });

          results.push(prom);
          cursor.continue();
        }
      };
    });

    db.close();
    return msgs;
  }

  async getLatestThreadMessages(accountID: string, starting: number, limit: number): Promise<Array<DisplayMessage>> {
    const db = await this.getDB();
    const msgs = await new Promise<DisplayMessage[]>((resolve, reject) => {

      const results: Promise<DisplayMessage>[] = [];
      const transaction = db.transaction("messages");

      transaction.onerror = function () { reject(transaction.error); };
      transaction.onabort = function () {
        console.warn("transaction aborted");
        reject(transaction.error);
      };
      transaction.oncomplete = function () { resolve(bluebird.all(results)); };

      let haveNotStarted = true;
      const objectStore = transaction.objectStore("messages");
      const index = objectStore.index("replyIdx");
      const upperBound = IDBKeyRange.lowerBound([accountID, new Date(0)]);
      const request = index.openCursor(upperBound, "prev");
      request.onerror = () => reject(request.error);
      request.onsuccess = evt => {
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        if (haveNotStarted && starting > 0) {
          cursor.advance(starting);
          haveNotStarted = false;
          return;
        }
        if (results.length < limit) {
          const msgContainer = cursor.value as MessageContainer; // save value, cursor gets cleaned before then() fires
          const prom = this.cryptoService.open(
            msgContainer.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
            .then(buf => deserialize(buf, { promoteBuffers: true, promoteValues: true }) as DisplayMessage)
            .then(msg => {
              return this.unpackMessage(msg, msgContainer.isUnread, msgContainer.isMention, msgContainer.participatingInThread) as DisplayMessage;
            });

          results.push(prom);
          cursor.continue();
        }
      };
    });

    db.close();
    return msgs;
  }

  async getNewestMsgDate(groupID: string): Promise<DateTime | null> {
    const db = await this.getDB();
    return await new Promise<DateTime | null>((resolve, reject) => {

      const transaction = db.transaction("messages");
      let result: Promise<DateTime | null> | null = null;

      transaction.onerror = function () { reject(transaction.error); };
      transaction.onabort = function () {
        console.warn("transaction aborted");
        reject(transaction.error);
      };
      transaction.oncomplete = function () { resolve(result); };

      const objectStore = transaction.objectStore("messages");
      const index = objectStore.index("group");
      const range = IDBKeyRange.only(groupID);
      const request = index.openCursor(range, "prev");
      request.onerror = () => reject(request.error);
      request.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }

        const msgContainer = cursor.value as MessageContainer; // save value, cursor gets cleaned before then() fires
        if (msgContainer.created && msgContainer.msgtype) {
          if (msgContainer.msgtype !== MessageType.CONTENT &&
            msgContainer.msgtype !== MessageType.REACTION) {
            cursor.continue();
            return;
          }
          result = bluebird.resolve(DateTime.fromJSDate(msgContainer.created));
        } else {
          result = this.cryptoService.open(
            msgContainer.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
            .then(buf => deserialize(buf, { promoteBuffers: true, promoteValues: true }) as DisplayMessage)
            .then(msg => {
              if (msg.msgtype !== MessageType.CONTENT && msg.msgtype !== MessageType.REACTION) {
                return null;
              }
              const created = (msg.created as any)._d ?
                DateTime.fromJSDate((msg.created as any)._d) :
                moment.isMoment(msg.created) ?
                  DateTime.fromJSDate(msg.created.toDate()) :
                  DateTime.isDateTime(msg.created) ?
                    msg.created :
                    DateTime.fromJSDate(msg.created);

              console.log("msg for group", groupID, "id:", msg._id, "created:", created.toISO());
              return created;
            });
        }
      };
    });
  }

  // can't hold the transaction open while re-encrypting the data
  async updateMessage(msgID: string, accountID: string, update: (msg: DisplayMessage | null) => Promise<DisplayMessage | null>): Promise<DisplayMessage> {
    const db = await this.getDB();
    const store = "messages";

    let frameID: string | undefined = undefined;
    let acctID: string | undefined = undefined;
    let unread: 0 | 1 = 0;
    let mention: 0 | 1 = 0;
    let participatingInThread: 0 | 1 = 0;
    // fetch the group in one transaction
    let message = await readWithID<DisplayMessage | null>(db, store, msgID, (result: any) => {
      if (!result) { return bluebird.resolve(null); }
      frameID = result.frameID;
      acctID = result.accountID;
      unread = result.isUnread;
      mention = result.isMention;
      participatingInThread = result.participatingInThread;
      return this.cryptoService.open(
        result.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
        .then(buf => {
          return deserialize(buf, { promoteBuffers: true, promoteValues: true }) as DisplayMessage;
        });
    });

    message = this.unpackMessage(message, unread, mention, participatingInThread);
    message = await update(message);

    if (!message) {
      throw new Error("Message not found");
    }
    message = this.prepareMessageForSave(message);

    // save the message in a second transaction
    const buf = await this.cryptoService.seal(serialize(message), this.decryptKey as CryptoKey, this.dbNonce);

    await updateTransaction(db, store, transaction => {
      const objectStore = transaction.objectStore(store);
      objectStore.put(this.messageContainerFor(acctID || accountID, frameID as string, message, buf));
    });
    db.close();
    return message;
  }

  async markMessagesAsRead(groupID: string, starting: number): Promise<void> {
    const db = await this.getDB();
    await new Promise<void>((resolve, reject) => {

      const transaction = db.transaction("messages", "readwrite");

      transaction.onerror = function () { reject(transaction.error); };
      transaction.onabort = function () {
        console.warn("transaction aborted");
        reject(transaction.error);
      };
      transaction.oncomplete = function () { resolve(); };

      let haveNotStarted = true;
      const objectStore = transaction.objectStore("messages");
      const index = objectStore.index("group");
      const range = IDBKeyRange.only(groupID);
      const request = index.openCursor(range, "prev");
      request.onerror = () => reject(request.error);
      request.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        if (haveNotStarted && starting > 0) {
          cursor.advance(starting);
          haveNotStarted = false;
          return;
        }

        if (cursor.value.isUnread) {
          const val = cursor.value;
          val.isUnread = 0;
          cursor.update(val);
        }
        cursor.continue();
      };
    });
    db.close();
  }

  deleteMessage(msgID: string): Promise<void> {
    return this.openAndWrite(["messages", "replies", "reactions"], (transaction: IDBTransaction) => {
      let objectStore = transaction.objectStore("messages");
      objectStore.delete(msgID);

      objectStore = transaction.objectStore("replies");
      const index = objectStore.index("thread");
      const range = IDBKeyRange.only(msgID);
      const request = index.openCursor(range);
      request.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        cursor.delete();
        cursor.continue();
      };

      objectStore = transaction.objectStore("reactions");

      const reactIndex = objectStore.index("message");
      const reactRange = IDBKeyRange.only(msgID);
      const reactRequest = reactIndex.openCursor(reactRange);

      reactRequest.onsuccess = evt => {
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        cursor.delete();
        cursor.continue();
      };
    });
  }

  prepareMessageForSave(message: DisplayMessage): DisplayMessage {
    if (moment.isMoment(message.created)) {
      message.created = message.created.toDate();
    } else if (DateTime.isDateTime(message.created)) {
      message.created = message.created.toJSDate();
    }
    if (message.lastReply) {
      if (moment.isMoment(message.lastReply)) {
        message.lastReply = message.lastReply.toDate();
      } else if (DateTime.isDateTime(message.lastReply)) {
        message.lastReply = message.lastReply.toJSDate();
      }
    }
    return message;
  }

  messageContainerFor(accountID: string, frameID: string, message: DisplayMessage, buf: Uint8Array): MessageContainer {
    const lastReply = !message.lastReply ? message.lastReply :
      moment.isMoment(message.lastReply) ? message.lastReply.toDate() :
        DateTime.isDateTime(message.lastReply) ? message.lastReply.toJSDate() :
          message.lastReply;

    return {
      _id: message._id,
      accountID: accountID,
      groupID: message.groupID,
      threadID: message.threadID,
      frameID: frameID,
      msgtype: message.msgtype,
      isUnread: message.isUnread ? 1 : 0,
      isMention: message.isMention ? 1 : 0,
      participatingInThread: message.participatingInThread ? 1 : 0,
      hideThread: 0,
      lastReply: lastReply || undefined,
      ciphertext: buf,
      created: message.created instanceof Date ? message.created :
        moment.isMoment(message.created) ? message.created.toDate() :
          message.created.toJSDate()
    };
  }

  unpackMessage(msg: DisplayMessage | null, isUnread: 0 | 1, isMention: 0 | 1, participatingInThread: 0 | 1): DisplayMessage | null {
    if (!msg) { return msg; }
    msg.isUnread = isUnread ? true : false;
    msg.isMention = isMention ? true : false;
    msg.participatingInThread = participatingInThread ? true : false;

    if ((msg.created as any)._d) {
      msg.created = DateTime.fromJSDate((msg.created as any)._d);
    } else if (moment.isMoment(msg.created)) {
      msg.created = DateTime.fromJSDate(msg.created.toDate());
    } else if (msg.created instanceof Date) {
      msg.created = DateTime.fromJSDate(msg.created);
    }

    if (msg.lastReply) {
      if (moment.isMoment(msg.lastReply)) {
        msg.lastReply = DateTime.fromJSDate(msg.lastReply.toDate());
      } else if (msg.lastReply instanceof Date) {
        msg.lastReply = DateTime.fromJSDate(msg.lastReply);
      }
    }

    return msg;
  }

  deleteContent(groupID: string): Promise<void> {
    return this.openAndWrite(["messages", "replies", "drafts", "reactions"], (transaction: IDBTransaction) => {
      let objectStore = transaction.objectStore("messages");
      const index = objectStore.index("group");
      const range = IDBKeyRange.only(groupID);
      const request = index.openCursor(range);
      request.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        cursor.delete();
        cursor.continue();
      };

      objectStore = transaction.objectStore("replies");
      const repliesIndex = objectStore.index("group");
      const replyReq = repliesIndex.openCursor(range);
      replyReq.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        cursor.delete();
        cursor.continue();
      };

      objectStore = transaction.objectStore("drafts");
      const draftIndex = objectStore.index("group");
      const draftReq = draftIndex.openCursor(range);
      draftReq.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        cursor.delete();
        cursor.continue();
      };

      objectStore = transaction.objectStore("reactions");
      const reactionIndex = objectStore.index("group");
      const reactionReq = reactionIndex.openCursor(range);
      reactionReq.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        cursor.delete();
        cursor.continue();
      };
    });
  }

  async saveReply(frameID: string, accountID: string, message: DisplayMessage): Promise<void> {
    message = this.prepareMessageForSave(message);
    message.frameID = frameID;
    const buf = await this.cryptoService.seal(serialize(message),
      this.decryptKey as CryptoKey, this.dbNonce)
    return await this.openAndWrite("replies", (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("replies");
      objectStore.put(this.messageContainerFor(accountID, frameID, message, buf));
    });
  }

  async getReplyFromFrame(frameID: string): Promise<DisplayMessage | null> {

    return this.openAndRead("replies", async (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("replies");
      let unread: 0 | 1 = 0;
      let mention: 0 | 1 = 0;
      let participatingInThread: 0 | 1 = 0;
      const buf = await new bluebird<Uint8Array | null>((resolve, reject) => {
        const index = objectStore.index("frame");
        const request = index.get(frameID);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          if (!request.result) {
            return resolve(null);
          }
          const container = request.result as MessageContainer;
          unread = container.isUnread;
          mention = container.isMention;
          participatingInThread = container.participatingInThread;
          return resolve(this.cryptoService.open(
            container.ciphertext, this.decryptKey as CryptoKey, this.dbNonce))
        }
      })

      if (!buf) { return buf; }
      return this.unpackMessage(
        deserialize(buf, { promoteBuffers: true, promoteValues: true }) as DisplayMessage,
        unread, mention, participatingInThread);
    });
  }

  async getReply(replyID: string): Promise<DisplayMessage | null> {
    return this._getMessage(replyID, "replies");
  }

  async countUnreadParticipatingThreadsForAccount(accountID: string): Promise<[number, number]> {
    const db = await this.getDB();
    const threads = await new Promise<string[]>((resolve, reject) => {
      const results: string[] = [];
      const transaction = db.transaction("messages");
      transaction.onerror = function () { reject(transaction.error); };
      transaction.onabort = function () {
        console.warn("transaction aborted");
        reject(transaction.error);
      };
      transaction.oncomplete = function () { resolve(results); };
      const objectStore = transaction.objectStore("messages");
      const index = objectStore.index("replyIdx");
      const upperBound = IDBKeyRange.lowerBound([accountID, new Date(0)]);
      const request = index.openCursor(upperBound, "prev");
      request.onerror = () => reject(request.error);
      request.onsuccess = evt => {
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) { return; }
        const container = cursor.value as MessageContainer;
        if (container.hideThread || container.participatingInThread <= 0) {
          cursor.continue();
          return;
        }
        results.push(container._id);
        cursor.continue();
      };
    });

    let totalUnread = 0;
    let totalMentions = 0;

    for (const threadID of threads) {
      const counts = await new Promise<[number, number]>((resolve, reject) => {
        let unread = 0;
        let mentions = 0;
        const transaction = db.transaction("replies");
        transaction.onerror = function () { reject(transaction.error); };
        transaction.onabort = function () {
          console.warn("transaction aborted");
          reject(transaction.error);
        };
        transaction.oncomplete = function () { resolve([unread, mentions]); };

        const objectStore = transaction.objectStore("replies");
        const index = objectStore.index("thread");
        const range = IDBKeyRange.only(threadID);
        const request = index.openCursor(range, "prev");
        request.onerror = () => reject(request.error);
        request.onsuccess = evt => {
          // called once per cursor value
          const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
          if (!cursor) { return; }
          const container = cursor.value as MessageContainer;
          if (!container.isUnread) {
            cursor.continue();
            return;
          }
          unread++;

          if (container.isMention) {
            mentions++;
          }
          cursor.continue();
        }
      });

      totalUnread += counts[0];
      totalMentions += counts[1];
    }

    db.close();
    return [totalUnread, totalMentions];
  }



  async getReplies(threadID: string): Promise<Array<DisplayMessage>> {
    const db = await this.getDB();
    const msgs = await new Promise<DisplayMessage[]>((resolve, reject) => {
      const transaction = db.transaction("replies");

      const results: Promise<DisplayMessage>[] = [];

      transaction.onerror = function () { reject(transaction.error); };
      transaction.onabort = function () {
        console.warn("transaction aborted");
        reject(transaction.error);
      };
      transaction.oncomplete = function () { resolve(bluebird.all(results)); };

      const objectStore = transaction.objectStore("replies");
      const index = objectStore.index("thread");
      const range = IDBKeyRange.only(threadID);
      const request = index.openCursor(range);
      request.onerror = () => reject(request.error);
      request.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        const msgContainer = cursor.value as MessageContainer; // save value, cursor gets cleaned before then() fires
        const prom = this.cryptoService.open(
          msgContainer.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
          .then(buf => deserialize(buf, { promoteBuffers: true, promoteValues: true }) as DisplayMessage)
          .then(msg =>
            this.unpackMessage(msg, msgContainer.isUnread, msgContainer.isMention, msgContainer.participatingInThread) as DisplayMessage
          );

        results.push(prom);
        cursor.continue();
      };
    });
    db.close();
    return msgs;
  }

  // can't hold the transaction open while re-encrypting the data
  async updateReply(replyID: string, update: (msg: DisplayMessage | null) => Promise<DisplayMessage | null>): Promise<DisplayMessage> {
    const db = await this.getDB();
    console.log("updating msg:", replyID);

    let frameID: string | undefined = undefined;
    let accountID: string | undefined = undefined;
    let threadID: string | undefined = undefined;
    let unread: 0 | 1 = 0;
    let mention: 0 | 1 = 0;
    let participatingInThread: 0 | 1 = 0;
    // fetch the reply in one transaction
    let reply = await readWithID<DisplayMessage | null>(db as IDBDatabase, "replies", replyID, (result: MessageContainer) => {
      if (!result) { return bluebird.resolve(null); }
      frameID = result.frameID;
      threadID = result.threadID;
      accountID = result.accountID;
      unread = result.isUnread;
      mention = result.isMention;
      participatingInThread = result.participatingInThread;
      return this.cryptoService.open(
        result.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
        .then(buf => {
          return deserialize(buf, { promoteBuffers: true, promoteValues: true }) as DisplayMessage;
        });
    });

    reply = this.unpackMessage(reply, unread, mention, participatingInThread);

    reply = await update(reply);

    if (!reply) {
      throw new Error("Reply not found");
    }
    reply = this.prepareMessageForSave(reply);
    if (threadID) {
      reply.threadID = threadID;
    }

    // save the reply in a second transaction
    const buf = await this.cryptoService.seal(serialize(reply), this.decryptKey as CryptoKey, this.dbNonce);

    await updateTransaction(db as IDBDatabase, "replies", (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("replies");
      objectStore.put(this.messageContainerFor(
        accountID as string, frameID as string, reply, buf));
    });

    db.close();
    return reply;
  }

  async deleteReply(replyID: string): Promise<void> {
    await this.openAndWrite("replies", (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("replies");
      objectStore.delete(replyID);
    });
  }

  async markThreadAsRead(threadID: string): Promise<boolean> {
    const db = await this.getDB();
    let changed = false;
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction("messages", "readwrite");

      transaction.onerror = function () { reject(transaction.error); };
      transaction.onabort = function () {
        console.warn("transaction aborted");
        reject(transaction.error);
      };
      transaction.oncomplete = function () { resolve(); };

      const objectStore = transaction.objectStore("messages");
      const request = objectStore.get(threadID);
      request.onsuccess = () => {

        const container = request.result as MessageContainer;
        if (container.isUnread) {
          container.isUnread = 0;
          objectStore.put(container);
          changed = true;
        }
      };
    });

    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction("replies", "readwrite");

      transaction.onerror = function () { reject(transaction.error); };
      transaction.onabort = function () {
        console.warn("transaction aborted");
        reject(transaction.error);
      };
      transaction.oncomplete = function () { resolve(); };

      const objectStore = transaction.objectStore("replies");
      const index = objectStore.index("thread");
      const range = IDBKeyRange.only(threadID);
      const request = index.openCursor(range, "prev");
      request.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        const val = cursor.value as MessageContainer;
        if (val.isUnread) {
          val.isUnread = 0;
          cursor.update(val);
          changed = true;
        }
        cursor.continue();
      };
    });
    db.close();
    return changed;
  }

  getReaction(reactionID: string): Promise<Reaction | null> {
    return this.openAndReadID("reactions", reactionID, async (result: any): Promise<Reaction | null> => {
      if (!result) { return null; }
      const buf = await this.cryptoService.open(
        result.ciphertext, this.decryptKey as CryptoKey, this.dbNonce);
      const reaction = deserialize(buf, { promoteBuffers: true, promoteValues: true }) as Reaction;
      return reaction;
    });
  }

  getRecentMainReactions(newerThan: Date | undefined, olderThan?: Date): Promise<Array<Reaction>> {
    if (!this.dbname) { return bluebird.resolve([]); }
    return this.openAndRead("reactions", transaction => {
      return new bluebird<Reaction[]>((resolve, reject) => {
        const results: Promise<Reaction>[] = [];
        const objectStore = transaction.objectStore("reactions");


        const request = newerThan ? (() => {
          const index = objectStore.index("created");
          const range = olderThan ?
            IDBKeyRange.bound(newerThan, olderThan, false, true) :
            IDBKeyRange.lowerBound(newerThan);
          return index.openCursor(range, "prev");
        })() : objectStore.openCursor(null, "prev");

        request.onerror = () => reject(request.error);
        request.onsuccess = evt => {
          // called once per cursor value
          const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
          if (cursor) {
            const val = cursor.value;
            if (val.threadID) {
              cursor.continue();
              return;
            }
            const prom = this.cryptoService.open(
              val.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
              .then(buf => deserialize(buf, { promoteBuffers: true, promoteValues: true }) as Reaction);
            results.push(prom);
            cursor.continue();
          } else {
            resolve(bluebird.all(results));
          }
        };
      });
    });
  }

  getReactionsForMessage(msgID: string): Promise<Array<Reaction>> {
    if (!this.dbname) { return bluebird.resolve([]); }
    return this.openAndRead("reactions", transaction => {
      return new bluebird<Reaction[]>((resolve, reject) => {
        const results: Promise<Reaction>[] = [];
        const objectStore = transaction.objectStore("reactions");

        const index = objectStore.index("message");
        const range = IDBKeyRange.only(msgID);
        const request = index.openCursor(range);

        request.onerror = () => reject(request.error);
        request.onsuccess = evt => {
          // called once per cursor value
          const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
          if (cursor) {
            const val = cursor.value;
            const prom = this.cryptoService.open(
              val.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
              .then(buf => deserialize(buf, { promoteBuffers: true, promoteValues: true }) as Reaction);
            results.push(prom);
            cursor.continue();
          } else {
            resolve(bluebird.all(results));
          }
        };
      });
    });
  }

  getReactionsForThread(threadID: string): Promise<Array<Reaction>> {
    if (!this.dbname) { return bluebird.resolve([]); }
    return this.openAndRead("reactions", transaction => {
      return new bluebird<Reaction[]>((resolve, reject) => {
        const results: Promise<Reaction>[] = [];
        const objectStore = transaction.objectStore("reactions");

        const index = objectStore.index("thread");
        const range = IDBKeyRange.only(threadID);
        const request = index.openCursor(range);

        request.onerror = () => reject(request.error);
        request.onsuccess = evt => {
          // called once per cursor value
          const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
          if (cursor) {
            const val = cursor.value;
            const prom = this.cryptoService.open(
              val.ciphertext, this.decryptKey as CryptoKey, this.dbNonce)
              .then(buf => deserialize(buf, { promoteBuffers: true, promoteValues: true }) as Reaction);
            results.push(prom);
            cursor.continue();
          } else {
            resolve(bluebird.all(results));
          }
        };
      });
    });
  }

  async saveReaction(accountID: string, groupID: string, reaction: Reaction): Promise<void> {
    const users = reaction.users;
    if (users) {
      delete reaction.users;
    }
    const buf = await this.cryptoService.seal(serialize(reaction),
      this.decryptKey as CryptoKey, this.dbNonce)
    const container = {
      _id: reaction._id,
      frameID: reaction.frameID,
      accountID: accountID,
      groupID: groupID,
      msgID: reaction.msgID,
      threadID: reaction.threadID,
      //msgFrameID: reaction.msgFrameID,
      created: DateTime.isDateTime(reaction.created) ? reaction.created.toJSDate() : reaction.created,
      ciphertext: buf
    };
    await this.openAndWrite("reactions", (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("reactions");
      objectStore.put(container);
    });
    reaction.users = users;
  }

  removeReaction(reactionID: string): Promise<void> {
    if (!this.dbname) { return bluebird.reject(new Error("userID not configured in DB")); }
    return this.openAndWrite("reactions", (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("reactions");
      objectStore.delete(reactionID);
    });
  }

  async getDraft(_id: string): Promise<MessageDraft | null> {
    return this.openAndRead<MessageDraft | null>("drafts", async (transaction: IDBTransaction) => {
      const encDraft = await new bluebird<MessageDraft | null>((resolve, reject) => {
        const objectStore = transaction.objectStore("drafts");
        const request = objectStore.get(_id);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });
      if (!encDraft) { return null; }

      const contentBuf = typeof encDraft.content === "string" ?
        new TextEncoder().encode(encDraft.content) : encDraft.content;
      const decrypted = await this.cryptoService.open(contentBuf, this.decryptKey as CryptoKey, this.dbNonce);
      return {
        ...encDraft,
        content: new TextDecoder().decode(decrypted),
        lastUpdated: DateTime.fromJSDate(encDraft.lastUpdated as Date)
      };
    });
  }

  async getDrafts(accountID: string): Promise<Array<MessageDraft>> {
    const db = await this.getDB();
    const drafts = await new Promise<MessageDraft[]>((resolve, reject) => {
      const transaction = db.transaction("drafts");

      const results: Promise<MessageDraft>[] = [];

      transaction.onerror = function () { reject(transaction.error); };
      transaction.onabort = function () {
        console.warn("transaction aborted");
        reject(transaction.error);
      };
      transaction.oncomplete = function () { resolve(bluebird.all(results)); };

      const objectStore = transaction.objectStore("drafts");
      const index = objectStore.index("account");
      const range = IDBKeyRange.only(accountID);
      const request = index.openCursor(range);
      const decoder = new TextDecoder();
      request.onerror = () => reject(request.error);
      request.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        const encDraft: MessageDraft = cursor.value; // save value, cursor gets cleaned before then() fires
        const prom = this.cryptoService.open(
          encDraft.content as Uint8Array, this.decryptKey as CryptoKey, this.dbNonce)
          .then(decrypted => {
            return {
              ...encDraft,
              content: decoder.decode(decrypted),
              lastUpdated: DateTime.fromJSDate(encDraft.lastUpdated as Date)
            };
          });

        results.push(prom);
        cursor.continue();
      };
    });
    db.close();
    return drafts;
  }

  async upsertDraft(draft: MessageDraft): Promise<void> {
    const buf = typeof draft.content === "string" ?
      new TextEncoder().encode(draft.content as string) : draft.content;
    const lastUpdated = DateTime.isDateTime(draft.lastUpdated) ?
      draft.lastUpdated.toJSDate() : draft.lastUpdated;
    return this._upsertDraft(draft._id, draft.accountID, draft.groupID, buf, lastUpdated);
  }
  async upsertDraftContent(accountID: string, groupID: string, threadID: string | null | undefined, content: string): Promise<void> {
    const buf = new TextEncoder().encode(content as string);
    return this._upsertDraft(threadID || groupID, accountID, groupID, buf, new Date());
  }
  async _upsertDraft(_id: string, accountID: string, groupID: string, content: Uint8Array, lastUpdated: Date): Promise<void> {

    const encrypted = await this.cryptoService.seal(
      content, this.decryptKey as CryptoKey, this.dbNonce);

    await this.openAndWrite("drafts", transaction => {
      const objectStore = transaction.objectStore("drafts");
      objectStore.put({
        _id: _id,
        groupID: groupID,
        accountID: accountID,
        content: encrypted,
        lastUpdated: lastUpdated
      });
    });
  }

  async deleteDraft(groupID: string, threadID: string | null | undefined): Promise<void> {
    return this.openAndWrite("drafts", (transaction: IDBTransaction) => {
      const objectStore = transaction.objectStore("drafts");
      objectStore.delete(threadID || groupID);
    });
  }
}

let instance: DbService;

export function dbServiceFactory(): DbService {
  if (!instance) { instance = new MsgDbServiceImpl(); }
  return instance;
}

/**
 * Used for testing purposes 
 * @returns a new db service imp
 */
export function dbServiceFactoryTest(): DbService {
  return new MsgDbServiceImpl();
}

type UpdaterFn = (db: IDBDatabase, oldVersion: number, req: IDBOpenDBRequest) => void;

function openDB(dbName: string, version: number, updater: UpdaterFn): Promise<IDBDatabase> {
  if (!dbName) {
    console.error("no dbname when trying to open");
  }
  return new bluebird<IDBDatabase>((resolve, reject) => {
    const dbRequest: IDBOpenDBRequest = indexedDB.open(dbName, version);
    dbRequest.onerror = (evt) => {
      console.error("Error opening db:", evt);
      reject(dbRequest.error);
    };

    dbRequest.onsuccess = () => {
      resolve(dbRequest.result);
    };

    dbRequest.onupgradeneeded = (evt: IDBVersionChangeEvent) => {
      updater(dbRequest.result, evt.oldVersion, dbRequest);
    };
  });
}

type ReadTransactionFn<T> = (transaction: IDBTransaction) => Promise<T>;

function readTransaction<T>(db: IDBDatabase, stores: string | string[], readFn: ReadTransactionFn<T>): Promise<T> {
  return new bluebird<any>((resolve, reject) => {
    const transaction = db.transaction(stores);
    transaction.onerror = function () {
      reject(transaction.error || new Error("Error reading from DB"));
    };
    transaction.onabort = function () {
      console.warn("transaction aborted");
      reject(transaction.error || new Error("Aborted Read Transaction"));
    };
    const prom = readFn(transaction);
    transaction.oncomplete = function () {
      resolve(prom);
    };
  });
}

function readWithID<T>(db: IDBDatabase, store: string, _id: string, readFn: (result: any) => T | Promise<T>): Promise<T> {
  return new bluebird<any>((resolve, reject) => {
    const transaction = db.transaction(store);
    transaction.onerror = function () {
      reject(transaction.error || new Error("Error reading from DB, store: " + store));
    };
    transaction.onabort = function () {
      console.warn("transaction aborted");
      reject(transaction.error || new Error("Aborted Read Transaction in store: " + store));
    };
    const objectStore = transaction.objectStore(store);

    const request = objectStore.get(_id);
    let prom: T | Promise<T>;
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      prom = readFn(request.result);
    }

    transaction.oncomplete = function () {
      resolve(prom);
    };
  });
}

type UpdateTransactionFn = (transaction: IDBTransaction) => void;

function updateTransaction(db: IDBDatabase, stores: string | string[], writeFn: UpdateTransactionFn): Promise<any> {
  return new bluebird<void>((resolve, reject) => {
    const transaction = db.transaction(stores, "readwrite");
    transaction.onerror = function () {
      reject(transaction.error);
    };
    transaction.onabort = function () {
      console.warn("transaction aborted");
      reject(transaction.error);
    };
    transaction.oncomplete = function () {
      resolve();
    };
    writeFn(transaction);
  });
}

export interface SavedTokens {
  _id: string, // should just be set to the userID, but in case we share userIDs across regions
  userID: string,
  regionID: string,
  access_token: string,
  refresh_token: string,
  expires: Date | string,
  received: Date,
  perms: UserScopes,
  needAuthMethod: boolean
}

export interface SaveState {
  userID: string,
  lastSync: Date,
  curMembershipID: string
}

export interface CredService {
  readonly DB_VERSION: number

  getDeviceID(userID: string): Promise<string | null>
  setDeviceID(userID: string, deviceID: string): Promise<void>
  getTokens(regionID: string, userID?: string): Promise<Array<SavedTokens>>
  saveTokens(tokens: SavedTokens): Promise<void>
  deleteTokens(userID: string): Promise<void>
  deleteAllTokens(region: string): Promise<void>
  getUserState(userID: string): Promise<SaveState>
  updateUserState(userID: string, update: (state: SaveState | null | undefined) => Promise<SaveState | null>): Promise<void>
  deleteUserState(userID: string): Promise<void>
  getMemberships(userID: string): Promise<Array<Membership>>
  saveMemberships(memberships: Array<Membership>): Promise<void>
  deleteMemberships(userID: string): Promise<void>
}

class CredServiceImpl extends ParentDBService implements CredService {
  static CRED_DB_VERSION = 2;
  get DB_VERSION(): number { return CredServiceImpl.CRED_DB_VERSION; }
  constructor() {
    super(CredServiceImpl.CRED_DB_VERSION);
    this.dbname = "credStore"
  }

  versionUpdater(db: IDBDatabase, oldVersion: number): void {
    let objectStore: IDBObjectStore;
    console.log("credDB, upgrading from version:", oldVersion);
    if (oldVersion < 1) {
      objectStore = db.createObjectStore("devices", { keyPath: "userID" });
      objectStore = db.createObjectStore("tokens", { keyPath: "_id" });
      objectStore.createIndex("region", "regionID", { unique: false });
      objectStore = db.createObjectStore("memberships", { keyPath: "_id" });
      objectStore.createIndex("region", "regionID", { unique: false });
      objectStore.createIndex("users", "userID", { unique: false });
    }
    if (oldVersion < CredServiceImpl.CRED_DB_VERSION) {
      objectStore = db.createObjectStore("saveState", { keyPath: "userID" });
    }
  }

  getDeviceID(userID: string): Promise<string | null> {
    return this.openAndReadID("devices", userID, (result): string | null => {
      if (!result) { return null; }
      return result.deviceID;
    });
  }

  setDeviceID(userID: string, deviceID: string): Promise<void> {
    const store = "devices";
    return this.openAndWrite(store, transaction => {
      const objectStore = transaction.objectStore(store);
      objectStore.put({
        userID: userID,
        deviceID: deviceID
      });
    });
  }

  getUserState(userID: string): Promise<SaveState> {
    const store = "saveState";
    return this.openAndReadID(store, userID, result => {
      return result;
    });
  }

  async updateUserState(userID: string, update: (state: SaveState | null | undefined) => Promise<SaveState | null>): Promise<void> {
    const db = await this.getDB();
    const store = "saveState";

    let saveState = await readWithID(db, store, userID, result => result);

    saveState = await update(saveState);

    await updateTransaction(db, store, transaction => {
      const objectStore = transaction.objectStore(store);
      if (saveState) {
        objectStore.put(saveState);
      } else {
        objectStore.delete(userID);
      }
    });
    db.close();
  }

  deleteUserState(userID: string): Promise<void> {
    const store = "saveState";
    return this.openAndWrite(store, transaction => {
      const objectStore = transaction.objectStore(store);
      objectStore.delete(userID);
    });
  }

  getTokens(regionID: string, userID?: string): Promise<Array<SavedTokens>> {
    const store = "tokens";
    return this.openAndRead(store, transaction => {
      return new bluebird<any>((resolve, reject) => {
        const results: Array<SavedTokens> = [];
        const objectStore = transaction.objectStore(store);
        const index = objectStore.index("region");
        const range = IDBKeyRange.only(regionID);
        const request = index.openCursor(range);
        request.onerror = () => reject(request.error);
        request.onsuccess = evt => {
          // called once per cursor value
          const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
          if (cursor) {
            const val = cursor.value as SavedTokens;
            if (!userID || val.userID == userID) {
              results.push(val);
            }
            cursor.continue();
          } else {
            results.sort((a, b) => {
              if (a.received > b.received) { return -1; }
              if (a.received < b.received) { return 1; }
              return 0;
            });
            resolve(bluebird.all(results));
          }
        };
      });
    })
  }

  saveTokens(tokens: SavedTokens): Promise<void> {
    const store = "tokens";
    return this.openAndWrite(store, transaction => {
      const objectStore = transaction.objectStore(store);
      objectStore.put(tokens);
    });
  }

  deleteTokens(userID: string): Promise<void> {
    const store = "tokens";
    return this.openAndWrite(store, transaction => {
      transaction.objectStore(store).delete(userID);
    });
  }

  deleteAllTokens(region: string): Promise<void> {
    const store = "tokens";
    return this.openAndWrite(store, transaction => {
      const objectStore = transaction.objectStore(store);
      const index = objectStore.index("region");
      const range = IDBKeyRange.only(region);
      const request = index.openCursor(range);
      request.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        cursor.delete();
        cursor.continue();
      };
    });
  }

  getMemberships(userID: string): Promise<Array<Membership>> {
    const store = "memberships";
    return this.openAndRead<Membership[]>(store, transaction => {
      return new bluebird<Membership[]>((resolve, reject) => {
        const results: Array<Membership> = [];
        const objectStore = transaction.objectStore(store);
        const index = objectStore.index("users");
        const range = IDBKeyRange.only(userID);
        const request = index.openCursor(range);
        request.onerror = () => reject(request.error);
        request.onsuccess = evt => {
          // called once per cursor value
          const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
          if (cursor) {
            const val = cursor.value as Membership;
            results.push(val);
            cursor.continue();
          } else {
            resolve(bluebird.all(results));
          }
        };
      });
    });
  }

  saveMemberships(memberships: Array<Membership>): Promise<void> {
    const store = "memberships";
    return this.openAndWrite(store, transaction => {
      const objectStore = transaction.objectStore(store);
      for (const membership of memberships) {
        objectStore.put(membership);
      }
    });
  }

  deleteMemberships(userID: string): Promise<void> {
    const store = "memberships";
    return this.openAndWrite(store, transaction => {
      const objectStore = transaction.objectStore(store);
      const index = objectStore.index("users");
      const range = IDBKeyRange.only(userID);
      const request = index.openCursor(range);
      request.onsuccess = evt => {
        // called once per cursor value
        const cursor: IDBCursorWithValue = (evt?.target as IDBRequest).result;
        if (!cursor) {
          return;
        }
        cursor.delete();
        cursor.continue();
      };
    });
  }
}

let credServiceInstance: CredService | null = null;

export function credServiceFactory(): CredService {
  if (!credServiceInstance) {
    credServiceInstance = new CredServiceImpl();
  }
  return credServiceInstance;
}

